import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_LABELS = { pending: "Pendente", confirmed: "Confirmado", completed: "Concluído", cancelled: "Cancelado" };
const STATUS_COLORS = { pending: "bg-yellow-100 text-yellow-800", confirmed: "bg-green-100 text-green-800", completed: "bg-blue-100 text-blue-800", cancelled: "bg-red-100 text-red-800" };

const emptyForm = { client_id: "", service_id: "", date: "", slot: "", status: "pending", notes: "", laser_details: { session_number: "", pch: "", valor: "" } };

export default function Agenda() {
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [slots, setSlots] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAppointments = () => api.get(`/admin/appointments${date ? `?date=${date}` : ""}`).then((r) => setAppointments(r.data)).catch(() => {});

  useEffect(() => {
    loadAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);
  useEffect(() => {
    api.get("/admin/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/admin/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.date) {
      api.get(`/public/availability/slots?date=${form.date}`).then((r) => setSlots(r.data)).catch(() => setSlots([]));
    }
  }, [form.date]);

  const isLaser = services.find((s) => s.id === form.service_id)?.slug === "depilacao-laser";

  const updateStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/appointments/${id}/status`, { status });
      if (data.email_sent === true) toast.success("E-mail enviado à cliente");
      else if (data.email_sent === false) toast.error("Status salvo, mas o e-mail à cliente não pôde ser enviado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    loadAppointments();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const body = { ...form };
      if (!isLaser) body.laser_details = null;
      else {
        body.laser_details = {
          session_number: form.laser_details.session_number ? Number(form.laser_details.session_number) : null,
          pch: form.laser_details.pch || null,
          valor: form.laser_details.valor ? Number(form.laser_details.valor) : null,
        };
      }
      await api.post("/admin/appointments", body);
      toast.success("Agendamento criado!");
      setOpen(false); setForm(emptyForm); loadAppointments();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="admin-agenda-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-serif text-[#9B6179]">Agenda</h1>
        <Button onClick={() => setOpen(true)} data-testid="new-appointment-button" className="bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors"><Plus className="w-4 h-4 mr-1" /> Novo Agendamento</Button>
      </div>

      <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-48 mb-4" data-testid="agenda-date-filter" />

      <div className="bg-white rounded-[2px] shadow-sm border border-[#9B6179]/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F0E8] text-[#6F3F55] text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Horário</th><th className="text-left px-4 py-3">Cliente</th><th className="text-left px-4 py-3">Serviço</th><th className="text-left px-4 py-3">Status</th></tr>
          </thead>
          <tbody>
            {appointments.map((a) => (
              <tr key={a.id} className="border-t border-[#9B6179]/5" data-testid={`agenda-row-${a.id}`}>
                <td className="px-4 py-3">{a.date} {a.slot}</td>
                <td className="px-4 py-3">{a.client_name}</td>
                <td className="px-4 py-3">{a.service_name}</td>
                <td className="px-4 py-3">
                  <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                    <SelectTrigger className="w-36 h-8" data-testid={`agenda-status-${a.id}`}>
                      <Badge className={STATUS_COLORS[a.status]}>{STATUS_LABELS[a.status]}</Badge>
                    </SelectTrigger>
                    <SelectContent>{Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
            {appointments.length === 0 && <tr><td colSpan={4} className="text-center py-8 text-[#6F3F55]">Nenhum agendamento nesta data.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-appointment-modal">
          <form onSubmit={submit} className="space-y-3">
            <p className="font-serif text-lg text-[#9B6179]">Novo Agendamento</p>
            <div>
              <Label>Cliente</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="appointment-form-client"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} — {c.email}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Serviço</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="appointment-form-service"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name_pt}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="appointment-form-date" /></div>
              <div>
                <Label>Horário</Label>
                <Select value={form.slot} onValueChange={(v) => setForm({ ...form, slot: v })}>
                  <SelectTrigger data-testid="appointment-form-slot"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                  <SelectContent>{slots.map((s) => <SelectItem key={s.slot} value={s.slot} disabled={!s.available}>{s.slot}{!s.available ? " (ocupado)" : ""}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {isLaser && (
              <div className="grid grid-cols-3 gap-2 bg-[#FAF0F0] p-3 rounded-[2px]">
                <div><Label className="text-xs">Sessão</Label><Input type="number" value={form.laser_details.session_number} onChange={(e) => setForm({ ...form, laser_details: { ...form.laser_details, session_number: e.target.value } })} data-testid="appointment-form-session" /></div>
                <div><Label className="text-xs">PCH</Label><Input value={form.laser_details.pch} onChange={(e) => setForm({ ...form, laser_details: { ...form.laser_details, pch: e.target.value } })} data-testid="appointment-form-pch" /></div>
                <div><Label className="text-xs">Valor (€)</Label><Input type="number" value={form.laser_details.valor} onChange={(e) => setForm({ ...form, laser_details: { ...form.laser_details, valor: e.target.value } })} data-testid="appointment-form-valor" /></div>
              </div>
            )}
            <Button disabled={saving || !form.client_id || !form.service_id || !form.date || !form.slot} type="submit" className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="appointment-form-submit">Criar Agendamento</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
